import { useState, type ReactNode } from 'react';
import { CalendarRange, Info, Users } from 'lucide-react';
import { Carte, Chargement, MessageErreur, Pastille, PastilleSite } from '../ui/Primitives';
import { jourLisible } from '../ecf/format';
import { usePrevisionFormateur } from '../../lib/useFormateurs';
import type {
  EtatMoisPrevision,
  LignePrevisionApprenant,
  LignePrevisionSession,
  MoisPropose,
} from '../../lib/useEspaceFormateur';

/**
 * Heures prévisionnelles du formateur (TF-419) : ce que partagent sa saisie
 * (onglet « Prévisionnel » de « Mes heures ») et la lecture du pôle dans
 * « Formateurs ». La déclaration du réalisé (TF-420) réutilise les lignes.
 * Tout vient de l'API — mois ouverts, périmètre, total.
 */

const ETATS: Record<EtatMoisPrevision, { libelle: string; ton: string }> = {
  OUVERT: { libelle: 'À saisir', ton: 'info' },
  FIGE: { libelle: 'Figé', ton: 'neutral' },
  A_VENIR: { libelle: 'À venir', ton: 'neutral' },
};

/** Le pôle lit sans saisir : un mois ouvert n'est pas « à saisir » pour lui. */
function etatLu(etat: EtatMoisPrevision, pole: boolean): { libelle: string; ton: string } {
  return pole && etat === 'OUVERT' ? { libelle: 'Saisie ouverte', ton: 'info' } : ETATS[etat];
}

export function PastilleEtatMois({ etat, pole = false }: { etat: EtatMoisPrevision; pole?: boolean }): JSX.Element {
  const { libelle, ton } = etatLu(etat, pole);
  return <Pastille texte={libelle} ton={ton} />;
}

/** Le choix du mois, avec l'état de chacun : prévisionnel ou déclaration du réalisé. */
export function ChoixMois({
  id,
  mois,
  options,
  onChange,
}: {
  id: string;
  mois: string;
  options: readonly { cle: string; libelle: string; etat: string }[];
  onChange: (mois: string) => void;
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <label htmlFor={id} className="text-xs font-semibold text-codialis-navy">
        Mois
      </label>
      <select
        id={id}
        value={mois}
        onChange={(evenement) => onChange(evenement.target.value)}
        className="min-h-[44px] min-w-0 rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
      >
        {options.map((option) => (
          <option key={option.cle} value={option.cle}>
            {option.libelle} · {option.etat.toLowerCase()}
          </option>
        ))}
      </select>
    </div>
  );
}

export function SelecteurMois({
  id,
  mois,
  proposes,
  onChange,
  pole = false,
}: {
  id: string;
  mois: string;
  proposes: MoisPropose[];
  onChange: (mois: string) => void;
  pole?: boolean;
}): JSX.Element {
  return (
    <ChoixMois
      id={id}
      mois={mois}
      options={proposes.map((propose) => ({ ...propose, etat: etatLu(propose.etat, pole).libelle }))}
      onChange={onChange}
    />
  );
}

function periode(debut: string | null, fin: string | null): string {
  if (debut && fin) return `du ${jourLisible(debut)} au ${jourLisible(fin)}`;
  if (debut) return `à partir du ${jourLisible(debut)}`;
  if (fin) return `jusqu’au ${jourLisible(fin)}`;
  return 'dates à fixer';
}

function MentionHorsPerimetre(): JSX.Element {
  return (
    <p className="mt-1 text-[11px] text-amber-800">
      N’est plus dans votre périmètre ce mois-ci : la ligne reste telle qu’elle a été saisie.
    </p>
  );
}

/**
 * Une ligne : sa cible à gauche, les heures (ou la case) à droite — trois
 * colonnes, prévu, réalisé et écart, quand elle est `large`. `empilee`, les
 * colonnes passent sous la cible à toute largeur : c'est la lecture du pôle,
 * dans la colonne étroite de la fiche du formateur.
 */
function Ligne({
  infos,
  heures,
  large,
  empilee,
}: {
  infos: ReactNode;
  heures: ReactNode;
  large: boolean;
  empilee: boolean;
}): JSX.Element {
  const disposition = empilee
    ? ''
    : large
      ? 'md:flex-row md:items-center md:justify-between'
      : 'sm:flex-row sm:items-center sm:justify-between';
  const colonnes = empilee
    ? 'min-w-0 sm:max-w-md'
    : large
      ? 'min-w-0 md:w-[22rem] md:shrink-0'
      : 'shrink-0 sm:w-40';
  return (
    <li className={`flex flex-col gap-3 px-4 py-3.5 sm:px-6 ${disposition}`}>
      <div className="min-w-0 flex-1">{infos}</div>
      <div className={colonnes}>{heures}</div>
    </li>
  );
}

/** Ce que la ligne désigne : la prévision ou la déclaration du réalisé. */
type Geste = 'prevision' | 'declaration';

export function LigneSession({
  ligne,
  heures,
  formateur = true,
  complement,
  empilee = false,
}: {
  ligne: Omit<LignePrevisionSession, 'heures'>;
  heures: ReactNode;
  /** Vue du formateur : le hors-périmètre se dit. */
  formateur?: boolean;
  /** Sous la cible : le repère émargé de la déclaration. Présent, la ligne s'élargit. */
  complement?: ReactNode;
  empilee?: boolean;
}): JSX.Element {
  return (
    <Ligne
      large={complement !== undefined}
      empilee={empilee}
      heures={heures}
      infos={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-codialis-navy">{ligne.libelle}</span>
            <PastilleSite site={ligne.siteCode} />
            {formateur && ligne.horsPerimetre && <Pastille texte="Hors périmètre" ton="warning" />}
          </div>
          <p className="mt-0.5 text-xs text-codialis-gray">
            {ligne.formation ?? 'Formation à préciser'} · {periode(ligne.debut, ligne.fin)}
          </p>
          <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-codialis-gray">
            <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {ligne.apprenants === 0
              ? 'Aucun apprenant rattaché pour l’instant'
              : `${ligne.apprenants} apprenant${ligne.apprenants > 1 ? 's' : ''} rattaché${ligne.apprenants > 1 ? 's' : ''} : la ligne vaut pour chacun`}
          </p>
          {complement}
          {formateur && ligne.horsPerimetre && <MentionHorsPerimetre />}
        </>
      }
    />
  );
}

export function LigneApprenant({
  ligne,
  heures,
  formateur = true,
  complement,
  geste = 'prevision',
  empilee = false,
}: {
  ligne: Omit<LignePrevisionApprenant, 'heures'>;
  heures: ReactNode;
  formateur?: boolean;
  complement?: ReactNode;
  geste?: Geste;
  empilee?: boolean;
}): JSX.Element {
  return (
    <Ligne
      large={complement !== undefined}
      empilee={empilee}
      heures={heures}
      infos={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-codialis-navy">{ligne.apprenant}</span>
            <PastilleSite site={ligne.siteCode} />
            {formateur && ligne.horsPerimetre && <Pastille texte="Hors périmètre" ton="warning" />}
          </div>
          <p className="mt-0.5 text-xs text-codialis-gray">
            {ligne.formation ?? 'Formation à préciser'} · {periode(ligne.debut, ligne.fin)}
          </p>
          {formateur && ligne.session && !ligne.horsPerimetre && (
            <p className="mt-0.5 text-xs text-codialis-gray">
              Session « {ligne.session.libelle} » : {geste === 'prevision' ? 'prévoyez' : 'déclarez'} ses heures ici
              ou sur la session, pas les deux.
            </p>
          )}
          {complement}
          {formateur && ligne.horsPerimetre && <MentionHorsPerimetre />}
        </>
      }
    />
  );
}

export function HeuresLues({ texte }: { texte: string | null }): JSX.Element {
  return (
    <span className={`block text-left text-sm font-bold sm:text-right ${texte ? 'text-codialis-navy' : 'text-codialis-gray'}`}>
      {texte ?? '—'}
    </span>
  );
}

export function ListeVide({ children }: { children: ReactNode }): JSX.Element {
  return <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">{children}</p>;
}

/**
 * Lecture du pôle, dans la fiche du formateur : ce qu'il a saisi, mois par
 * mois. Le pôle ne saisit pas à sa place.
 */
export function CartePrevisionFormateur({ formateurId }: { formateurId: string }): JSX.Element {
  const [mois, setMois] = useState<string | null>(null);
  const prevision = usePrevisionFormateur(formateurId, mois);
  const vue = prevision.data;
  const vide = vue && vue.sessions.length === 0 && vue.apprenants.length === 0;

  return (
    <Carte
      titre="Heures prévisionnelles"
      actions={
        vue ? (
          <>
            <PastilleEtatMois etat={vue.etat} pole />
            <SelecteurMois
              id={`mois-prevision-${formateurId}`}
              mois={vue.mois}
              proposes={vue.moisProposes}
              onChange={setMois}
              pole
            />
          </>
        ) : undefined
      }
      sansBordure
    >
      {prevision.isLoading && (
        <div className="p-4 sm:p-6">
          <Chargement />
        </div>
      )}
      {prevision.isError && (
        <div className="p-4 sm:p-6">
          <MessageErreur message="Les heures prévisionnelles n’ont pas pu être lues." />
        </div>
      )}
      {vue && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-gray-100 px-4 py-3 text-sm sm:px-6">
            <span className="inline-flex items-center gap-1.5 text-codialis-gray">
              <CalendarRange className="h-4 w-4 shrink-0" aria-hidden />
              {vue.libelle}
            </span>
            <span className="text-codialis-gray">
              Total prévu <strong className="text-codialis-navy">{vue.total.texte}</strong>
            </span>
          </div>
          {vide ? (
            <ListeVide>
              {vue.etat === 'OUVERT'
                ? `Rien de saisi pour ${vue.libelle} : le formateur peut encore le faire.`
                : `Aucune heure prévue pour ${vue.libelle}.`}
            </ListeVide>
          ) : (
            <ul className="divide-y divide-gray-100">
              {vue.sessions.map((ligne) => (
                <LigneSession
                  key={ligne.sessionId}
                  ligne={ligne}
                  formateur={false}
                  heures={<HeuresLues texte={ligne.heures?.texte ?? null} />}
                />
              ))}
              {vue.apprenants.map((ligne) => (
                <LigneApprenant
                  key={ligne.apprenantId}
                  ligne={ligne}
                  formateur={false}
                  heures={<HeuresLues texte={ligne.heures?.texte ?? null} />}
                />
              ))}
            </ul>
          )}
          {vue.lignesNonAffichees > 0 && (
            <p className="flex gap-2 border-t border-gray-100 px-4 py-3 text-xs text-codialis-gray sm:px-6">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
              <span className="min-w-0">
                {vue.lignesNonAffichees} ligne{vue.lignesNonAffichees > 1 ? 's' : ''} d’une autre antenne
                {vue.lignesNonAffichees > 1 ? ' ne sont' : ' n’est'} pas affichée{vue.lignesNonAffichees > 1 ? 's' : ''} :
                elle{vue.lignesNonAffichees > 1 ? 's sont' : ' est'} hors de votre périmètre.
              </span>
            </p>
          )}
        </>
      )}
    </Carte>
  );
}
